/**
 * @fileoverview Plan limit alert component.
 * @summary Displays an alert when plan limit is reached.
 *
 * @package KaraoNow
 * @module apps/front/src/components/PlanLimitAlert
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useState, type FC } from 'react'

interface PlanLimitAlertProps {
  /** Whether the limit is reached (songs or events) */
  limitReached: boolean
  /** Current count (songs or events) */
  currentCount: number
  /** Plan limit (max songs/events) */
  limit: number
  /** Type of limit: 'songs' or 'events' */
  limitType: 'songs' | 'events'
  /** Callback when upgrade button is clicked */
  onUpgrade: () => void
  /** Additional className */
  className?: string
}

/**
 * @summary Alert banner shown when plan limit is reached.
 * @param {PlanLimitAlertProps} props - Component props.
 * @returns {ReactElement} Alert banner or null.
 */
const PlanLimitAlert: FC<PlanLimitAlertProps> = ({
  limitReached,
  currentCount,
  limit,
  limitType,
  onUpgrade,
  className = '',
}) => {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(false)

  if (!limitReached || dismissed) {
    return null
  }

  const getMessage = () => {
    if (limitType === 'songs') {
      return t('planAlert.songsLimitReached', { limit, currentCount })
    } else {
      return t('planAlert.eventsLimitReached', { limit, currentCount })
    }
  }

  return (<>
    <div
      className={`bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg  ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-4 mb-4">
      {/* Icon */}
      <div className="shrink-0 pt-0.5">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-yellow-600 text-lg"
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-yellow-800 mb-1">
          {t('planAlert.limitReached')}
        </h3>
        <p className="text-sm text-yellow-700">{getMessage()}</p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 shrink-0">
        
        <button
          onClick={() => setDismissed(true)}
          className="px-2 py-2 text-yellow-600 hover:text-yellow-800 transition-colors duration-200"
          title={t('common.close')}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      </div>
        <button
      onClick={onUpgrade}
      className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
    >
      {t('planAlert.upgrade')}
    </button>
    </div>
    </>
  )
}

export default PlanLimitAlert

